/**
 * @file api-transfers.ts
 * @description Admin API-клиент документа «Перемещение» (phase19.23 этап B).
 *
 * Зеркалит `apps/api/src/wms/transfers/transfers.controller.ts` и
 * `transfer-include.ts`: список — конверт `{ data, meta }` (серверная
 * пагинация), карточка — документ целиком со строками. Любая мутация строки
 * возвращает документ заново, поэтому клиенту не нужно склеивать состояние
 * руками — один источник правды остаётся на сервере.
 *
 * Коды ошибок, которые разбирает UI, лежат в `wms-error-text.ts`; отдельно
 * обрабатывается только 409 `lot_moved` — у него есть действие «Обновить
 * строки» (`refreshRows`).
 */

import {
  apiDownload,
  apiFetch,
  apiFetchEnvelope,
  apiFetchWithMeta,
  type ApiFileResponse,
} from "./api-client";
import type { WmsSkuRef } from "./api-wms";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Черновик → проведён → отменён. Проведённый документ не отменяется (W15). */
export type TransferStatus = "DRAFT" | "COMMITTED" | "CANCELLED";

/** Откуда пришёл документ: руками, быстрым действием или листом размещения. */
export type TransferSource = "MANUAL" | "QUICK_ACTION" | "PLACEMENT";

/** Ячейка в строке документа. */
export interface TransferBinRef {
  id: string;
  code: string;
  label: string | null;
}

/** Строка документа: одна партия (или беспартийная позиция). */
export interface TransferItemDto {
  id: string;
  transferId: string;
  skuId: string;
  /** Снимок артикула на момент добавления строки. */
  skuCode: string;
  lotId: string | null;
  lotBatchNumber: string | null;
  fromBinId: string | null;
  fromBinCode: string | null;
  toWarehouseId: string;
  toBinId: string | null;
  toBinCode: string | null;
  toWarehouseName: string | null;
  toResponsibleName: string | null;
  quantity: string;
  isFullLot: boolean;
  postedAt: string | null;
  notes: string | null;
  createdAt: string;
  sku?: WmsSkuRef;
  /** `lot.binId` — фактический адрес партии сейчас (сверка со снимком). */
  lot?: {
    id: string;
    batchNumber: string;
    expiresAt: string | null;
    binId: string | null;
  } | null;
  toWarehouse?: {
    id: string;
    name: string;
    code: string;
    kind: string;
    isSellable: boolean;
  };
  fromBin?: TransferBinRef | null;
  toBin?: TransferBinRef | null;
}

/**
 * Позиция приёмки-основания, которую лист размещения взять не смог: у неё нет
 * партии, а адрес хранения в системе принадлежит партии (phase19.24, решение
 * пользователя 2026-09-20). Единицы измерения в контракте нет — количество
 * приходит строкой ровно так, как его показала приёмка.
 */
export interface TransferUnplaceableItemDto {
  skuCode: string;
  name: string;
  quantity: string;
}

/** Документ целиком — карточка. */
export interface TransferDto {
  id: string;
  shopId: string;
  transferNumber: string;
  status: TransferStatus;
  source: TransferSource;
  fromWarehouseId: string;
  refReceiptId: string | null;
  createdById: string;
  committedById: string | null;
  committedAt: string | null;
  cancelledById: string | null;
  cancelledAt: string | null;
  notes: string | null;
  fromWarehouseName: string | null;
  fromResponsibleName: string | null;
  createdAt: string;
  updatedAt: string;
  fromWarehouse?: {
    id: string;
    name: string;
    code: string;
    kind: string;
    responsibleUser?: { id: string; displayName: string } | null;
  };
  createdBy?: { id: string; displayName: string };
  committedBy?: { id: string; displayName: string } | null;
  cancelledBy?: { id: string; displayName: string } | null;
  /** Приёмка-основание листа размещения. */
  receipt?: { id: string; receiptNumber: string } | null;
  items?: TransferItemDto[];
  /**
   * Только у листа размещения (`source = PLACEMENT`, `refReceiptId`): позиции
   * приёмки без партии — их в документ не взяли. Опционально: старый сервер
   * поля не отдаёт, тогда полосу не показываем.
   */
  unplaceableItems?: TransferUnplaceableItemDto[];
}

/** Элемент реестра: строк целиком нет, только «куда» и количества. */
export interface TransferListItemDto {
  id: string;
  transferNumber: string;
  status: TransferStatus;
  source: TransferSource;
  fromWarehouseId: string;
  refReceiptId: string | null;
  committedAt: string | null;
  cancelledAt: string | null;
  notes: string | null;
  createdAt: string;
  fromWarehouse?: { id: string; name: string; code: string };
  createdBy?: { id: string; displayName: string };
  /**
   * Приёмка-основание листа размещения: сервер отдаёт её и в реестре, чтобы
   * строка читалась как «Лист размещения · RCP-2026-000007» со ссылкой, а не
   * дёргала карточку приёмки на каждый документ списка.
   */
  receipt?: { id: string; receiptNumber: string } | null;
  items?: {
    id: string;
    quantity: string;
    toWarehouseId: string;
    toWarehouse?: { id: string; name: string };
  }[];
}

/** Страница реестра: `meta` может не прийти со старого сервера. */
export interface TransfersPage {
  items: TransferListItemDto[];
  total: number;
  page: number;
  pageSize: number;
}

/** Фильтры реестра; поиск, статус, склад, период и страницы считает сервер. */
export interface ListTransfersParams {
  search?: string;
  status?: TransferStatus;
  fromWarehouseId?: string;
  /**
   * Один вид или список видов. Не задан — сервер сам прячет быстрые действия,
   * поэтому «обычные документы» это отсутствие параметра, а не перечисление
   * `MANUAL,PLACEMENT`.
   */
  source?: TransferSource | TransferSource[];
  /** Начало периода по `createdAt`: ISO-строка, `2026-09-01` или `Date`. */
  from?: string | Date;
  /** Конец периода, включающий: дата без времени сервер трактует как конец дня. */
  to?: string | Date;
  page?: number;
  pageSize?: number;
}

/** Тело `POST /:id/items`. */
export interface AddTransferItemPayload {
  skuId: string;
  lotId?: string;
  toWarehouseId: string;
  /** `null` — «без адреса» (склад без ячеек либо размещение позже). */
  toBinId?: string | null;
  /** Не задано — весь остаток партии на складе-источнике. */
  quantity?: string;
  notes?: string;
}

/** Тело `PATCH /:id/items/:itemId` — правка строки на месте. */
export interface UpdateTransferItemPayload {
  toWarehouseId?: string;
  toBinId?: string | null;
  quantity?: string;
  notes?: string;
}

/**
 * Одна партия листа размещения из журнала остатков (phase19.24 этап 1, шаг 4).
 *
 * `quantity` не задан — уезжает весь остаток партии: внутри склада партия и
 * так переезжает целиком (`partial_move_not_allowed`), поле оставлено на
 * случай, когда сервер разрешит частичное размещение.
 */
export interface PlacementItemPayload {
  lotId: string;
  toBinId: string;
  quantity?: string;
}

/**
 * Тело `POST /admin/wms/transfers/placement` — набор «партия → ячейка» одним
 * документом. `commit: true` проводит его сразу: из журнала остатков кладовщик
 * раскладывает товар, а не заводит черновик, который потом надо искать.
 */
export interface PlacementPayload {
  warehouseId: string;
  items: PlacementItemPayload[];
  /**
   * Провести сразу. Атомарности у проведения НЕТ (находка ревью 2026-09-20):
   * упав на строке, сервер оставляет документ черновиком и называет его в
   * `details.transferId` / `details.transferNumber` — UI обязан дать на него
   * ссылку, а не писать «повторите попытку».
   */
  commit?: boolean;
  /** Комментарий документа. */
  note?: string;
}

/**
 * Партия, которую сервер пропустил при размещении: она уже лежит в той
 * ячейке, куда её просили положить (решение пользователя 2026-09-20).
 *
 * Пропуск — не ошибка: остальные строки проводятся, документа на пропущенные
 * партии нет. Кладовщику это показывается полосой, а не красной ошибкой.
 */
export interface PlacementSkippedItem {
  lotId: string;
  batchNumber: string;
  skuCode: string;
  /** Ячейка, в которой партия уже лежит. */
  binCode: string;
  reason: "already_in_bin";
}

/**
 * Ответ листа размещения по приёмке: черновик и позиции, которые в него не
 * попали (правка контракта 2026-09-20).
 *
 * `transfer = null` — документа нет вовсе: размещать было нечего. Раньше
 * сервер в этом случае заводил пустой черновик, и в журнале копились MV без
 * строк.
 */
export interface PlacementDraftResult {
  transfer: TransferDto | null;
  /** Позиции приёмки без партии — адрес хранения им задать нельзя. */
  unplaceableItems: TransferUnplaceableItemDto[];
}

/** Ответ размещения: документ (или его отсутствие) и пропущенные партии. */
export interface PlacementResult {
  /** `null` — пропущены все партии, проводить было нечего (HTTP 200). */
  transfer: TransferDto | null;
  skipped: PlacementSkippedItem[];
}

/** 409 при проведении: партия уехала между черновиком и подписью. */
export const TRANSFER_LOT_MOVED_CODE = "lot_moved";

/** Строка расхождения из `details.items` ошибки `lot_moved`. */
export interface TransferLotMovedItem {
  itemId: string | null;
  lotId: string | null;
  batchNumber: string | null;
  expectedBinId: string | null;
  actualBinId: string | null;
  actualBinCode: string | null;
}

// ─── Endpoints ────────────────────────────────────────────────────────────────

/**
 * Граница периода в query: `Date` → ISO, строку отдаём как есть (сервер сам
 * достраивает край дня у формы `2026-09-01`). Пустая строка = «не задано».
 */
function toQueryDate(value: string | Date | undefined): string | undefined {
  if (!value) return undefined;
  return value instanceof Date ? value.toISOString() : value.trim() || undefined;
}

export const transfersApi = {
  /** Реестр со серверными фильтрами и пагинацией. */
  listPage: async (params: ListTransfersParams = {}): Promise<TransfersPage> => {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 20;
    const q = new URLSearchParams();
    if (params.search) q.set("search", params.search);
    if (params.status) q.set("status", params.status);
    if (params.fromWarehouseId) q.set("fromWarehouseId", params.fromWarehouseId);
    // Список видов уходит одной строкой через запятую: `URLSearchParams.set`
    // повторами не умеет, а сервер разбирает обе формы (`parseTransferSources`).
    const sources = params.source
      ? Array.isArray(params.source)
        ? params.source
        : [params.source]
      : [];
    if (sources.length > 0) q.set("source", sources.join(","));
    const from = toQueryDate(params.from);
    const to = toQueryDate(params.to);
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    q.set("page", String(page));
    q.set("pageSize", String(pageSize));
    const { data, meta } = await apiFetchWithMeta<
      TransferListItemDto[],
      { total: number; page: number; pageSize: number } | undefined
    >(`/admin/wms/transfers?${q.toString()}`);
    return {
      items: data ?? [],
      total: meta?.total ?? data?.length ?? 0,
      page: meta?.page ?? page,
      pageSize: meta?.pageSize ?? pageSize,
    };
  },
  findOne: (id: string) => apiFetch<TransferDto>(`/admin/wms/transfers/${id}`),
  create: (input: { fromWarehouseId: string; notes?: string }) =>
    apiFetch<TransferDto>("/admin/wms/transfers", {
      method: "POST",
      json: input,
    }),
  /** Правка шапки — только комментарий: склад-источник задаёт состав строк. */
  update: (id: string, input: { notes?: string | null }) =>
    apiFetch<TransferDto>(`/admin/wms/transfers/${id}`, {
      method: "PATCH",
      json: input,
    }),
  /** Отмена черновика: документ остаётся в реестре со статусом «Отменено». */
  cancel: (id: string) =>
    apiFetch<TransferDto>(`/admin/wms/transfers/${id}/cancel`, {
      method: "POST",
    }),
  commit: (id: string) =>
    apiFetch<TransferDto>(`/admin/wms/transfers/${id}/commit`, {
      method: "POST",
    }),
  /** Действие «Обновить строки» из диалога `lot_moved`. */
  refreshRows: (id: string) =>
    apiFetch<TransferDto>(`/admin/wms/transfers/${id}/refresh-rows`, {
      method: "POST",
    }),
  addItem: (id: string, payload: AddTransferItemPayload) =>
    apiFetch<TransferDto>(`/admin/wms/transfers/${id}/items`, {
      method: "POST",
      json: payload,
    }),
  updateItem: (id: string, itemId: string, payload: UpdateTransferItemPayload) =>
    apiFetch<TransferDto>(`/admin/wms/transfers/${id}/items/${itemId}`, {
      method: "PATCH",
      json: payload,
    }),
  removeItem: (id: string, itemId: string) =>
    apiFetch<TransferDto>(`/admin/wms/transfers/${id}/items/${itemId}`, {
      method: "DELETE",
    }),
  /**
   * Лист размещения по проведённой приёмке: черновик со всеми партиями без
   * адреса. Живёт здесь, а не в `api-wms`, потому что возвращает перемещение;
   * повторный вызов не плодит документы — сервер дописывает недостающие
   * строки в уже существующий черновик.
   *
   * `data: null` (правка контракта 2026-09-20) — размещать нечего: ни одной
   * партии без адреса в приёмке нет, и пустой документ сервер больше не
   * создаёт. Причина лежит рядом, в `unplaceableItems`: позиции без партии.
   */
  createPlacementDraft: async (receiptId: string): Promise<PlacementDraftResult> => {
    const envelope = await apiFetchEnvelope<{
      data?: TransferDto | null;
      unplaceableItems?: TransferUnplaceableItemDto[];
    }>(`/admin/wms/receipts/${receiptId}/placement-draft`, { method: "POST" });
    return {
      transfer: envelope.data ?? null,
      // Старый сервер список рядом с `data` не кладёт — читаем и из документа,
      // где он жил раньше.
      unplaceableItems:
        envelope.unplaceableItems ?? envelope.data?.unplaceableItems ?? [],
    };
  },
  /**
   * Размещение партий из журнала остатков: один документ MV со строками
   * «партия → ячейка», по умолчанию сразу проведённый (phase19.24 этап 1,
   * шаг 4).
   *
   * Отличие от `createPlacementDraft`: там основание — приёмка и состав
   * собирает сервер, здесь состав пришёл из отбора «Без места», который собрал
   * кладовщик руками.
   *
   * Ошибка по строке (422 из реестра перемещений, 409 `lot_moved`) документ НЕ
   * откатывает: он остаётся черновиком, а его id и номер приходят в `details`
   * (см. `parsePlacementError`). Вызывающий обязан перечитать отбор — часть
   * партий могла уже переехать.
   */
  placement: async (payload: PlacementPayload): Promise<PlacementResult> => {
    // Конверт нестандартный: `skipped` лежит рядом с `data`, поэтому читаем
    // его целиком. Оба поля защитные — старый сервер их может не прислать.
    const envelope = await apiFetchEnvelope<{
      data?: TransferDto | null;
      skipped?: PlacementSkippedItem[];
    }>("/admin/wms/transfers/placement", { method: "POST", json: payload });
    return { transfer: envelope.data ?? null, skipped: envelope.skipped ?? [] };
  },
  /**
   * Накладная на перемещение (PDF). Сервер отдаёт её только по проведённому
   * документу с межскладскими строками (иначе `transfer_internal_only`).
   */
  printNote: (id: string, number: string): Promise<ApiFileResponse> =>
    apiDownload(
      `/admin/wms/transfers/${id}/note.pdf`,
      { headers: { Accept: "application/pdf" } },
      `transfer-note-${number}.pdf`,
    ),
};

// ─── Разбор деталей и подписи ─────────────────────────────────────────────────

/** Подписи статусов — реестром, чтобы список и карточка не разъехались. */
export const TRANSFER_STATUS_LABEL: Record<TransferStatus, string> = {
  DRAFT: "Черновик",
  COMMITTED: "Проведено",
  CANCELLED: "Отменено",
};

/** Подписи источников документа. */
export const TRANSFER_SOURCE_LABEL: Record<TransferSource, string> = {
  MANUAL: "Вручную",
  QUICK_ACTION: "Быстрое действие",
  PLACEMENT: "Лист размещения",
};

/**
 * Расхождения из `details` ошибки `lot_moved`. Формат сервера проверяем
 * по месту: старый сервер мог не прислать деталей, и диалог тогда не нужен.
 */
export function parseLotMovedItems(details: unknown): TransferLotMovedItem[] {
  if (!details || typeof details !== "object") return [];
  const raw = (details as { items?: unknown }).items;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((row): row is Record<string, unknown> => !!row && typeof row === "object")
    .map((row) => ({
      itemId: typeof row.itemId === "string" ? row.itemId : null,
      lotId: typeof row.lotId === "string" ? row.lotId : null,
      batchNumber: typeof row.batchNumber === "string" ? row.batchNumber : null,
      expectedBinId:
        typeof row.expectedBinId === "string" ? row.expectedBinId : null,
      actualBinId: typeof row.actualBinId === "string" ? row.actualBinId : null,
      actualBinCode:
        typeof row.actualBinCode === "string" ? row.actualBinCode : null,
    }));
}
